import { trackSwapScannedTestBuyArticle } from "@coworker/app/src/helpers/tracker";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { Trans } from "@coworker/locales";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMyStore } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useUserPreference } from "@coworker/app/src/hooks/useProfilePreferencesQuery";
import profilePreferences from "@coworker/enums/profilePreferences";
import { replaceTestbuyArticle, Testbuy } from "../../services/testbuy.service";
import { replaceTestbuyArticleAndRefetch } from "../../services/queryKeyHelper";
import { TestbuyItemSearch } from "../TestbuyItemSearch/TestbuyItemSearch";
import { ExtractedArticleInformationType } from "@coworker/apprestructured/src/scan-and-search/types/ExtractedArticleInformationType";
import { useState } from "react";
import { ScannerErrorComponent } from "@ingka-group-digital/flexa-scanner";
import { TestBuyProductWithCriteria } from "@coworker/types/lib/testbuy";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { useTranslation } from "react-i18next";

interface ChangeWithButtonProps {
  testbuy: Testbuy;
  article: TestBuyProductWithCriteria;
  area: string;
  onClose?: () => void;
}

export const ChangeWithButton = ({
  testbuy,
  article,
  area,
  onClose,
}: ChangeWithButtonProps) => {
  // State
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hooks
  const { showToast } = useToastNotification();
  const store = useMyStore();
  const [userLanguage] = useUserPreference(
    profilePreferences.LANGUAGE,
    store?.primaryLocale
  );
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const replaceArticleMutation = useMutation({
    mutationFn: async (newArticleShortId: string) => {
      if (!article.id) return;
      return replaceTestbuyArticle(testbuy.id, article.id, {
        locale: userLanguage,
        countryId: store?.countryId ?? "",
        articleShortId: newArticleShortId,
      });
    },
    onSuccess: async (newArticle) => {
      if (newArticle) {
        await replaceTestbuyArticleAndRefetch(
          queryClient,
          testbuy.id,
          article.shortId,
          newArticle.shortId
        );
      }
      setIsSearching(false);
    },
  });

  // Event handlers
  const handleChangeWithClick = () => {
    trackSwapScannedTestBuyArticle(testbuy.strategy, article.shortId, area);
    setIsSearching(true);
  };

  const handleScanResult = async (
    itemInfo: ExtractedArticleInformationType
  ) => {
    const itemNo = itemInfo.articleNumber;
    const allTestbuyItemNos = testbuy.articles.map((a) => a.shortId);
    if (allTestbuyItemNos.includes(itemNo)) {
      setError("ARTICLE_ALREADY_EXISTS");
      setIsSearching(false);
    } else {
      try {
        await replaceArticleMutation.mutateAsync(itemNo);
        onClose?.();
      } catch {
        showToast(
          <Trans>couldNotUseThisArticleString</Trans>,
          undefined,
          undefined,
          5
        );
        setIsSearching(false);
      }
    }
  };

  if (!article.id) return null;
  return (
    <>
      <TestbuyItemSearch
        setIsSearching={setIsSearching}
        isSearching={isSearching}
        handleScanResult={handleScanResult}
      />
      <ScannerErrorComponent error={error} setError={setError} />
      <FixaListViewItem
        title={t("searchArticleString")}
        control="navigational"
        controlIcon={null}
        onClick={handleChangeWithClick}
        disabled={replaceArticleMutation.isPending}
        emphasised
      />
    </>
  );
};
